import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  Link,
  IconButton,
  Dialog,
  Tabs,
  Tab,
  Paper,
  Divider,
  Avatar,
  Chip,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SettingsPageLayout from './settings/SettingsPageLayout';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import api, { enhancedApi } from '../api';
import AddUserPage from './AddUserPage';
import AddTeamForm from './AddTeamForm';
import TeamsTab from './TeamsTab';
import RolesTab from './RolesTab';
import ClientAssignmentsDialog from './ClientAssignments';
import { Link as RouterLink } from 'react-router-dom';

const predefinedRoles = [
  { value: 'admin', label: 'Admin' },
  { value: 'admin_no_billing', label: 'Admin (No billing)' },
  { value: 'all_access', label: 'All access' },
  { value: 'custom_ai_settings', label: 'Custom AI settings' },
  { value: 'staff_all_clients', label: 'Staff (All clients)' },
  { value: 'staff_all_clients_no_billing', label: 'Staff (All clients, No billing)' },
  { value: 'staff_assigned_clients', label: 'Staff (Assigned clients)' },
  { value: 'staff_assigned_clients_no_billing', label: 'Staff (Assigned clients, No billing)' },
];

const displayName = (user) => {
  const fn = user.first_name || '';
  const ln = user.last_name || '';
  const composed = `${fn} ${ln}`.trim();
  return composed || user.username || user.email || 'Member';
};

const initialsOf = (user) => {
  const n = displayName(user);
  const p = n.split(/\s+/).filter(Boolean);
  if (p.length >= 2) return `${p[0][0]}${p[1][0]}`.toUpperCase();
  return n.slice(0, 2).toUpperCase();
};

const memberRoleChipProps = (user) => {
  if (user.profile?.permission_type === 'PERMISSION_SET' && user.profile?.permission_set) {
    return {
      label: user.profile.permission_set.name || 'Custom',
      color: 'info',
      variant: 'outlined',
    };
  }
  const role = user.profile?.role || '';
  if (role === 'all_access') return { label: 'Owner', color: 'secondary', variant: 'filled' };
  if (role.startsWith('admin')) return { label: 'Admin', color: 'info', variant: 'filled' };
  return { label: 'Staff', color: 'default', variant: 'outlined' };
};

function TeamOrganization() {
  const [users, setUsers] = useState([]);
  const [permissionSets, setPermissionSets] = useState([]);
  const [filter, setFilter] = useState('active');
  const [activeTab, setActiveTab] = useState('users');
  const [openInviteDialog, setOpenInviteDialog] = useState(false);
  const [openTeamDialog, setOpenTeamDialog] = useState(false);
  const [openClientAssignmentsDialog, setOpenClientAssignmentsDialog] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [error, setError] = useState('');
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuUser, setMenuUser] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchUsers();
    fetchPermissionSets();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount
  }, []);

  useEffect(() => {
    if (activeTab === 'teams') {
      fetchTeams();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tab-driven refresh only
  }, [activeTab]);

  const refreshToken = async () => {
    try {
      const tok = localStorage.getItem('refresh_token');
      if (!tok) return false;
      const response = await api.post('/api/token/refresh/', { refresh: tok });
      if (response.data.access) {
        localStorage.setItem('access_token', response.data.access);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const fetchTeams = async () => {
    try {
      await enhancedApi.getTeams();
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) return fetchTeams();
        setError('Please log in to view teams');
        navigate('/login');
      } else {
        setError('Failed to load teams');
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await enhancedApi.getUsers();
      let userList = [];
      if (Array.isArray(response.data)) {
        userList = response.data;
      } else if (Array.isArray(response.data?.results)) {
        userList = response.data.results;
      }
      setUsers(userList);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) return fetchUsers();
        setError('Please log in to view users');
        navigate('/login');
      } else {
        setError('Failed to load users');
      }
    }
  };

  const fetchPermissionSets = async () => {
    try {
      const response = await enhancedApi.getPermissionSets();
      setPermissionSets(response.data);
      setError('');
    } catch (err) {
      if (err.response?.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) return fetchPermissionSets();
        setError('Please log in to view permission sets');
        navigate('/login');
      } else {
        setError('Failed to load permission sets');
      }
    }
  };

  const handlePermissionChange = async (userId, value) => {
    let user_id = localStorage.getItem('userId');
    if (!user_id) {
      const raw = localStorage.getItem('user');
      if (raw) {
        try {
          const userData = JSON.parse(raw);
          user_id = userData.id || userData.user_id;
        } catch {
          user_id = null;
        }
      }
    }

    if (!user_id) {
      setError('Please log in to update permissions');
      navigate('/login');
      return;
    }

    try {
      if (value.startsWith('role:')) {
        const role = value.split('role:')[1];
        await enhancedApi.updateUser(userId, {
          profile: {
            permission_type: 'ROLE',
            role,
            permission_set: null,
          },
        });
        setUsers((prev) =>
          prev.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  profile: {
                    ...user.profile,
                    permission_type: 'ROLE',
                    role,
                    permission_set: null,
                  },
                }
              : user
          )
        );
      } else if (value.startsWith('permission_set:')) {
        const permissionSetId = parseInt(value.split('permission_set:')[1], 10);
        await enhancedApi.updateUser(userId, {
          profile: {
            permission_type: 'PERMISSION_SET',
            role: null,
            permission_set: permissionSetId,
          },
        });
        setUsers((prev) =>
          prev.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  profile: {
                    ...user.profile,
                    permission_type: 'PERMISSION_SET',
                    role: null,
                    permission_set: permissionSets.find((ps) => ps.id === permissionSetId),
                  },
                }
              : user
          )
        );
      }
    } catch (err) {
      if (err.response?.status === 401) {
        const refreshed = await refreshToken();
        if (refreshed) return handlePermissionChange(userId, value);
        setError('Please log in to update permissions');
        navigate('/login');
      } else {
        setError('Failed to update permission');
      }
    }
  };

  const filteredUsers = Array.isArray(users)
    ? users.filter((user) => {
        if (filter === 'inactive') return false;
        if (filter === 'deleted') return user.profile?.is_deleted;
        return true;
      })
    : [];

  const handleOpenClientAssignments = (userId) => {
    setSelectedUserId(userId);
    setOpenClientAssignmentsDialog(true);
    setMenuAnchor(null);
    setMenuUser(null);
  };

  const selectValueForUser = (user) =>
    user.profile?.permission_type === 'ROLE' || !user.profile?.permission_type
      ? `role:${user.profile?.role || 'staff_assigned_clients'}`
      : `permission_set:${user.profile?.permission_set?.id || ''}`;

  return (
    <SettingsPageLayout
      title="Team Members"
      description="Invite colleagues, assign roles, and manage access to clients."
      maxWidth={1000}
      noCard
    >
      <Paper variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', bgcolor: 'background.paper', overflow: 'hidden' }}>
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            borderBottom: 1,
            borderColor: 'divider',
            gap: 1,
          }}
        >
          {activeTab === 'users' && (
            <Button variant="contained" color="primary" startIcon={<PersonAddIcon />} onClick={() => setOpenInviteDialog(true)}>
              Invite Member
            </Button>
          )}
          {activeTab === 'teams' && (
            <Button variant="contained" color="primary" startIcon={<GroupOutlinedIcon />} onClick={() => setOpenTeamDialog(true)}>
              Create team
            </Button>
          )}
        </Box>

        {error ? (
          <Typography variant="body2" color="error" sx={{ px: 2, py: 1 }}>
            {error}
          </Typography>
        ) : null}

        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          sx={{
            px: 2,
            pt: 1,
            minHeight: 44,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { fontWeight: 600, fontSize: 14, textTransform: 'none' },
            '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0' },
          }}
        >
          <Tab label="Members" value="users" />
          <Tab label="Teams" value="teams" />
          <Tab label="Roles" value="roles" />
        </Tabs>

        {activeTab === 'users' && (
          <Table size="medium" sx={{ '& .MuiTableCell-root': { borderColor: 'divider' } }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 13, color: 'text.secondary' }}>Member</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13, color: 'text.secondary' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13, color: 'text.secondary' }}>Role</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13, color: 'text.secondary' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13, color: 'text.secondary' }}>Last login</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, fontSize: 13, color: 'text.secondary' }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => {
                  const chip = memberRoleChipProps(user);
                  return (
                    <TableRow key={user.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 36, height: 36, fontSize: 14, bgcolor: 'primary.light' }}>{initialsOf(user)}</Avatar>
                          <Link
                            component={RouterLink}
                            to={`/profile?user=${user.id}`}
                            underline="hover"
                            color="primary"
                            fontWeight={600}
                          >
                            {displayName(user)}
                          </Link>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.email || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Select
                          value={selectValueForUser(user)}
                          onChange={(e) => handlePermissionChange(user.id, e.target.value)}
                          size="small"
                          fullWidth
                          renderValue={() => (
                            <Chip
                              label={chip.label}
                              color={chip.color}
                              variant={chip.variant || 'filled'}
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        >
                          <MenuItem disabled value="">
                            — Roles —
                          </MenuItem>
                          {predefinedRoles.map((role) => (
                            <MenuItem key={role.value} value={`role:${role.value}`}>
                              {role.label}
                            </MenuItem>
                          ))}
                          <MenuItem disabled value="">
                            — Custom sets —
                          </MenuItem>
                          {permissionSets.length > 0 ? (
                            permissionSets.map((ps) => (
                              <MenuItem key={ps.id} value={`permission_set:${ps.id}`}>
                                {ps.name}
                              </MenuItem>
                            ))
                          ) : (
                            <MenuItem disabled value="">
                              None
                            </MenuItem>
                          )}
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.is_active === false ? 'Inactive' : 'Active'}
                          color={user.is_active === false ? 'default' : 'success'}
                          size="small"
                          variant={user.is_active === false ? 'outlined' : 'filled'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.profile?.last_login ? new Date(user.profile.last_login).toLocaleString() : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          aria-label="Row actions"
                          onClick={(e) => {
                            setMenuAnchor(e.currentTarget);
                            setMenuUser(user);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                      No members found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}

        {activeTab === 'teams' && (
          <Box sx={{ p: 2 }}>
            <TeamsTab />
          </Box>
        )}
        {activeTab === 'roles' && (
          <Box sx={{ p: 2 }}>
            <RolesTab />
          </Box>
        )}

        <Divider />
        <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          {activeTab === 'users' && (
            <>
              <Typography
                variant="body2"
                sx={{ cursor: 'pointer', color: filter === 'inactive' ? 'primary.main' : 'text.secondary', fontWeight: 500 }}
                onClick={() => setFilter('inactive')}
              >
                Inactive
              </Typography>
              <Typography
                variant="body2"
                sx={{ cursor: 'pointer', color: filter === 'deleted' ? 'primary.main' : 'text.secondary', fontWeight: 500 }}
                onClick={() => setFilter('deleted')}
              >
                Deleted
              </Typography>
            </>
          )}
        </Box>

        <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
          <MenuItem
            onClick={() => {
              if (menuUser) handleOpenClientAssignments(menuUser.id);
            }}
          >
            <ListItemIcon>
              <GroupOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Client assignments</ListItemText>
          </MenuItem>
          <MenuItem
            component={RouterLink}
            to={menuUser ? `/profile?user=${menuUser.id}` : '/settings/profile'}
            onClick={() => setMenuAnchor(null)}
          >
            <ListItemText>View profile</ListItemText>
          </MenuItem>
        </Menu>

        <Dialog open={openInviteDialog} onClose={() => setOpenInviteDialog(false)} fullWidth maxWidth="sm">
          <AddUserPage
            onInviteSubmit={() => {
              setOpenInviteDialog(false);
              fetchUsers();
            }}
            onClose={() => setOpenInviteDialog(false)}
            isDialog
          />
        </Dialog>
        <Dialog open={openTeamDialog} onClose={() => setOpenTeamDialog(false)} fullWidth maxWidth="sm">
          <AddTeamForm
            open={openTeamDialog}
            onClose={() => setOpenTeamDialog(false)}
            onTeamSubmit={() => {
              setOpenTeamDialog(false);
              fetchTeams();
            }}
          />
        </Dialog>
        <ClientAssignmentsDialog
          userId={selectedUserId}
          open={openClientAssignmentsDialog}
          onClose={() => {
            setOpenClientAssignmentsDialog(false);
            setSelectedUserId(null);
            fetchUsers();
          }}
        />
      </Paper>
    </SettingsPageLayout>
  );
}

export default TeamOrganization;
